import { restApi } from '../../api/restApiSlice';

export type GscConnectResponse = {
  authorizationUrl: string;
};

/** Where a source sits in its lifecycle, as visibility reports it. */
export type GscSourceState = 'ACTIVE' | 'PENDING' | 'DISCONNECTED' | 'ERROR';

/**
 * The two source types a GSC connection creates: the GSC source that pulls
 * Search Console data and its companion PRERENDER source. `/gsc/status`
 * returns both and nothing else.
 */
export type GscSourceType = 'GSC' | 'PRERENDER';

export type GscDisconnectReason = 'user' | 'token_revoked' | 'replaced' | 'access_lost';

/** One source row of the connection status response. */
export type GscSourceStatus = {
  id: string;
  type: GscSourceType;
  status: GscSourceState;
  propertyUrl: string | null;
  lastSyncAt: string | null;
  /**
   * Visibility's note on a DISCONNECTED source. `replaced` carries one too;
   * reconnect uses `disconnectReason`, falling back here only when that field
   * is omitted or null. Optional because a rest-api predating the field omits it.
   */
  statusMessage?: string | null;
  /**
   * Google account that granted this source. Optional because a rest-api
   * predating the field omits it; the Integrations card then counts every
   * GSC property rather than filtering to the connected account.
   */
  connectedAccountEmail?: string | null;
  /** `statusMessage` is the fallback when this is null. */
  disconnectReason?: GscDisconnectReason | null;
};

export type GscConnectionStatusResponse = {
  isConnected: boolean;
  /** Aggregated account email. Absent on older rest-api. */
  connectedAccountEmail?: string | null;
  sources: GscSourceStatus[];
};

export type GscProperty = {
  siteUrl: string;
  permissionLevel?: string;
  // False when the account's Search Console access is too low to read this
  // property. Optional because a rest-api that predates the field omits it —
  // consumers must fail open (`!== false`), mirroring the backend's fail-open
  // on an unknown permission level, or deploy skew empties the picker.
  isPermissionSufficient?: boolean;
  // The closest-scope representation of a domain Google offers more than one
  // way — rendered as a "Recommended" badge. Optional because a rest-api that
  // predates the field omits it; absence means no badge, never an error.
  recommended?: boolean;
};

export type SyncJob = {
  action: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
  recordsProcessed: number;
  startedAt: string;
  completedAt: string | null;
  metadata: Record<string, unknown> | null;
};

export type GscSourceDetail = {
  id: string;
  status: GscSourceState;
  propertyUrl: string | null;
  lastSyncAt: string | null;
  connectedAt: string | null;
  recentJobs: SyncJob[];
};

export type GscSelectionSessionResponse = {
  /** Only properties matching an active Prerender domain. */
  properties: GscProperty[];
  expiresAt: string;
  previouslySelectedUrls: string[];
  /** Aggregated account email. Absent on older rest-api. */
  connectedAccountEmail?: string | null;
  // Count *before* Prerender-domain matching. Zero separates "no Search Console
  // properties at all" from "has some, none match" — different next steps.
  totalPropertyCount: number;
  /** Sample of the properties that matched no Prerender domain. */
  unmatchedSiteUrls: string[];
};

/**
 * A selection session minted outside the OAuth round trip, so an already
 * connected customer can re-open the domain picker. rest-api resolves the donor
 * source from the tenant's live GSC sources; `sourceId` is the seed to activate
 * against.
 */
export type GscMintedSelectionSession = {
  sessionId: string;
  sourceId: string;
  expiresAt: string;
};

export type ActivateGscPayload = {
  source_id: string;
  selection_session_id: string;
  property_urls: string[];
  // Confirms replacing the live property that already syncs a selected domain
  // (the 409 domain-already-connected replace offer). Never sent unprompted.
  replace?: boolean;
};

export type ActivateGscFailedProperty = {
  property_url: string;
  error: 'companion-prerender-failed' | 'gsc-activation-unknown';
  message: string;
};

/**
 * `paired` are properties whose GSC and Prerender sources both exist now;
 * `failed` were left with no GSC source at all (PRE-3609). A definitive
 * activation failure still arrives as a 4xx with the guard bodies below.
 */
export type ActivateGscResponse = {
  paired: { property_url: string }[];
  failed: ActivateGscFailedProperty[];
  // Half-paired properties the tenant already had, repaired as a side effect
  // (not selected, so never part of `paired`). Absent on an older rest-api.
  repaired?: { property_url: string }[];
  repair_failed?: { property_url: string; message: string }[];
};

const gscConnectionApi = restApi.injectEndpoints({
  endpoints: (builder) => ({
    // Kicks off the GSC OAuth flow: rest-api creates the visibility source and
    // returns Google's consent URL. The caller redirects the browser to it.
    connectGsc: builder.mutation<GscConnectResponse, void>({
      query: () => ({ url: '/v3/visibility/gsc/connect', method: 'POST' }),
    }),
    getGscConnectionStatus: builder.query<GscConnectionStatusResponse, void>({
      query: () => ({ url: '/v3/visibility/gsc/status', method: 'GET' }),
      providesTags: ['GscConnectionStatus'],
    }),
    // After Google consent, the properties Google exposed for the grant.
    getGscSelectionSession: builder.query<GscSelectionSessionResponse, string>({
      query: (sessionId) => ({
        url: `/v3/visibility/gsc/selection-sessions/${sessionId}`,
        method: 'GET',
      }),
    }),
    mintGscSelectionSession: builder.mutation<GscMintedSelectionSession, void>({
      query: () => ({ url: '/v3/visibility/gsc/selection-sessions', method: 'POST' }),
      // Re-read the status even on failure: a `token_revoked` 409 means the donor
      // was just flipped to DISCONNECTED upstream.
      invalidatesTags: ['GscConnectionStatus'],
    }),
    activateGsc: builder.mutation<ActivateGscResponse, ActivateGscPayload>({
      query: (body) => ({ url: '/v3/visibility/gsc/activate', method: 'POST', body }),
      invalidatesTags: ['GscConnectionStatus'],
    }),
    getGscSourceDetail: builder.query<GscSourceDetail, string>({
      // Type-agnostic source endpoint (GSC and PRERENDER sources alike).
      query: (sourceId) => ({
        url: `/v3/visibility/sources/${sourceId}`,
        method: 'GET',
      }),
    }),
    // Disconnects the whole integration: rest-api disconnects every GSC
    // source and every companion PRERENDER source.
    disconnectGsc: builder.mutation<void, void>({
      query: () => ({ url: '/v3/visibility/gsc/disconnect', method: 'POST' }),
      invalidatesTags: ['GscConnectionStatus'],
    }),
    // Soft-disconnects one synced property: the GSC source for it and its
    // companion PRERENDER source, leaving the rest of the connection alone.
    disconnectGscProperty: builder.mutation<void, { propertyUrl: string }>({
      query: ({ propertyUrl }) => ({
        url: '/v3/visibility/gsc/properties/disconnect',
        method: 'POST',
        body: { property_url: propertyUrl },
      }),
      invalidatesTags: ['GscConnectionStatus'],
    }),
    // Soft-deletes a single source by ID (type-agnostic endpoint). Used to
    // clean up a PENDING source when the user goes back from the
    // domain-selection step to re-authenticate.
    deleteGscSource: builder.mutation<void, string>({
      query: (sourceId) => ({ url: `/v3/visibility/sources/${sourceId}`, method: 'DELETE' }),
      invalidatesTags: ['GscConnectionStatus'],
    }),
  }),
});

export { gscConnectionApi };
export const {
  useConnectGscMutation,
  useGetGscConnectionStatusQuery,
  useLazyGetGscConnectionStatusQuery,
  useGetGscSelectionSessionQuery,
  useGetGscSourceDetailQuery,
  useMintGscSelectionSessionMutation,
  useActivateGscMutation,
  useDisconnectGscMutation,
  useDisconnectGscPropertyMutation,
  useDeleteGscSourceMutation,
} = gscConnectionApi;
